import React, { useContext, useEffect, Suspense } from 'react';
import { withRouter } from 'react-router-dom';
import { cloneDeep, isEmpty, orderBy } from 'lodash';
import styled from 'styled-components';
import { El } from '@nile/reports/src/modules/reports/components/subpages/NewReportGrid/styles';
import {
  getDefaultChartType,
  getPossibleChartTypes
} from '@nile/reports/src/modules/charts/utils/chart-transformation';
import { ExportContext, BrandingLogo, Logo } from '@nile/components';
import { TextStyle } from '@superops/ui-design-system';
import SummaryTable from '@nile/reports/src/modules/charts/components/subpages/SummaryTable';
import {
  NUMBER,
  SUMMARY_TABLE,
  GRAPH_WIDTH,
  GRAPH_HEIGHT
} from '@nile/reports/src/modules/charts/constants';
import NumberChart from '@nile/reports/src/modules/charts/components/subpages/NumberChart';
import envConfig from '@nile/home/src/Config/env';
import { RETAINER_REPORT } from '@nile/reports/src/modules/home/constants';
import { SharedRetainerReportSheet } from '@nile/reports/src/modules/RetinerReport/components/pages/RetainerReportSheet';

const ReactECharts = React.lazy(() => import('echarts-for-react'));

const Wrapper = styled.div`
  .chart-card {
    border: 1px solid #80808026;
    margin: 10px;
  }
  .no-layout {
    background-image: url(${envConfig.imageRoot + 'svg/reportEmpty.svg'});
    background-repeat: no-repeat;
    background-size: 30%;
    background-position: center;
    width: 100%;
    height: calc(100vh - 110px);
  }
  .chart-title {
    font-weight: 500;
    font-size: 20px;
  }
  @media print {
    .hidebtn {
      display: none !important;
      visibility: hidden !important;
    }
    .chart-card canvas,
    .echarts-for-react > div {
      max-width: 650px;
      max-height: 400px;
      width: auto !important;
    }
  }
  .echart {
    .echarts-for-react {
      overflow: auto;
    }
  }
`;

const PrintReport = (props) => {
  const { isExport, data } = props;
  const exportContext = useContext(ExportContext);

  useEffect(() => {
    if (data) {
      exportContext.setIsRendered(true);
      exportContext.setReportName(data.name);
      document.body.style.overflow = 'auto';
      ['#root', '#root > div', '#root > div > div:nth-child(2)'].forEach(
        (selector) => {
          // Overriding styles from `index.css`
          document.querySelector(selector).style.overflow = 'scroll';
          document.querySelector(selector).style.minHeight = 'unset';
          document.querySelector(selector).style.maxHeight = 'unset';
          document.querySelector(selector).style.height = 'unset';
        }
      );
      if (!data?.layout?.layout.length) exportContext.setLoading(false);
    }
  }, []);

  return (
    <>
      <Wrapper>
        <div className="fx p20">
          <div className="flex brandingLogo">
            <BrandingLogo />
          </div>
        </div>
        {data?.reportName === 'Retainer Report' ? (
          <SharedRetainerReportSheet data={data} />
        ) : (
          <div
            className={
              !isEmpty(data.layout.layout) ? 'grid-layout' : 'no-layout'
            }
          >
            {orderBy(data?.layout?.layout || {}, ['y', 'x'], 'asc').map(
              (l, i) => (
                <>
                  <El
                    key={l.i}
                    isExport={isExport}
                    className="visibilityContainer"
                  >
                    <div
                      style={{
                        height: '100%',
                        pageBreakInside:
                          l?.chart?.defaultChartType === SUMMARY_TABLE
                            ? 'auto'
                            : 'avoid'
                      }}
                    >
                      <div
                        className={`center chart-card bg-white p20 chart-card${i}`}
                      >
                        <div>
                          {l?.chart?.name ? (
                            <div className="ellipsis" title={l.chart.name}>
                              <span className="chart-title">
                                {l.chart.name}
                              </span>
                            </div>
                          ) : (
                            <TextStyle variation="subdued">NA</TextStyle>
                          )}
                          <RenderChart
                            item={l}
                            size={{
                              width: 1280,
                              height: 520
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </El>
                </>
              )
            )}
          </div>
        )}
      </Wrapper>
    </>
  );
};

const RenderChart = ({ item, size }) => {
  const chartType =
    (getPossibleChartTypes(
      item.chart.metrics?.length,
      item.chart.groupBy?.length
    ).find((type) => type.name === item.chart.defaultChartType) &&
      item.chart.defaultChartType) ||
    getDefaultChartType(item.chart.metrics?.length, item.chart.groupBy?.length);

  const LABEL_WIDTH = 0.3;

  const getFormattedOptions = (opts) => {
    const formattedOptions = cloneDeep(opts);
    if (formattedOptions?.xAxis && formattedOptions?.xAxis?.axisLabel) {
      formattedOptions.xAxis.axisLabel.width = size.height * LABEL_WIDTH;
    }
    formattedOptions.grid = {
      left: '5%',
      right: '30%',
      containLabel: true
    };
    if (!formattedOptions.series) {
      formattedOptions.series = [{}];
    }
    formattedOptions.legend = {
      textStyle: {
        overflow: 'break',
        width: '100'
      },
      right: '5%',
      top: '10%',
      type: 'scroll',
      orient: 'vertical'
    };
    if (formattedOptions.series[0].type === 'pie') {
      delete formattedOptions.legend;
    }
    return formattedOptions;
  };

  if (chartType === NUMBER) {
    return <NumberChart data={item.chartOptions} />;
  }
  if (chartType === SUMMARY_TABLE) {
    return (
      <SummaryTable
        data={item.chartOptions}
        groups={item.chart.groupBy}
        metrics={item.chart.metrics}
        entity={item.chart.entity}
        chartRawData={item?.chartRawData}
      />
    );
  }
  if (!item?.chartOptions) {
    return (
      <div className="flex actr jctr full-height">
        <Logo
          url={`${envConfig.imageRoot}svg/chartEmpty.svg`}
          height="250px"
          width="250px"
        />
      </div>
    );
  }
  return (
    <div className="echart">
      <Suspense fallback={<i>loading...</i>}>
        <ReactECharts
          option={getFormattedOptions(item?.chartOptions)}
          opts={{
            width: `${GRAPH_WIDTH}px`,
            height: `${GRAPH_HEIGHT}px`,
            renderer: 'canvas'
          }}
          notMerge
          lazyUpdate
          style={{ height: item?.opts?.height }}
        />
      </Suspense>
    </div>
  );
};

export default withRouter(PrintReport);
